import {
    Component,
    Input,
    ContentChildren,
    TemplateRef,
    QueryList,
    AfterContentInit,
    EventEmitter,
    Output
} from '@angular/core';
import { ImageModel } from '../../models/image.model';
import { TemplateDirective } from '../../../Layout/directives/template/template.directive';
import { KeywordHighlightModel } from '../../models/keyword-highlight.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';

export const imageListComponentText = {
    imageNoLongerAvailable: 'Image no longer available from source'
};

@Component({
    selector: 'agpr-image-list',
    templateUrl: './image-list.component.html',
    styleUrls: ['./image-list.component.scss'],
    standalone: false
})
export class ImageListComponent implements AfterContentInit {

    readonly buttonTypesEnum = ButtonTypesEnum;

    @Input() public localization = imageListComponentText;
    @Input() public set images (images: ImageModel[]) {
        this._images = images || [];
        this.findMatches();
    }
    @Input() public showPopover: boolean = false;
    @Input() public set highlightKeywords(keywords: KeywordHighlightModel[]) {
        this._highlightKeywords = keywords || [];
        this.findMatches();
    }
    @Input() public showPreferredImageButton: boolean = false;
    @Input() public useMentionCardErrorStyle: boolean = false;

    @ContentChildren(TemplateDirective) templates: QueryList<TemplateDirective>;

    @Output() imageLoaded: EventEmitter<void> = new EventEmitter<void>();
    @Output() preferredImageChanged: EventEmitter<ImageModel> = new EventEmitter<ImageModel>();

    imageErrorTemplate: TemplateRef<any> = null;
    _highlightKeywords: KeywordHighlightModel[] = [];
    _images: ImageModel[] = [];
    _matches: {count: number; best: string}[] = [];
    readonly messageSeverities = messageSeverityEnum;

    /**
     * Returns true or false based on whether an error template exists
    */
    public get hasImageErrorTemplate(): boolean {
        const hasImageErrorTemplate: boolean = this.imageErrorTemplate !== null && this.imageErrorTemplate !== undefined;

        return hasImageErrorTemplate;
    }

    /**
     * Sets the image error template if it was provided by the parent component
    */
    public ngAfterContentInit() {
        this.templates.forEach(currentTemplate => {
            switch (currentTemplate.getType()) {
                case 'imageError':
                    this.imageErrorTemplate = currentTemplate.template;
                    break;
            }
        });
    }

    /**
     * Marks the provided image as having failed to load due to an error
    */
    public imageFailed(image: ImageModel): void {
        image.error = true;
        this.imageLoaded.emit();
    }

    public findMatches(): void {
        if (this._images.length < 1 || this._highlightKeywords.length < 1) {
            this._matches = [];
        }

        const allKeywords: string[] = [];
        this._highlightKeywords.forEach(keywordsModel => allKeywords.push(...keywordsModel.keywords));

        this._matches = this._images.map(image => {
            if (!image.metadata || image.metadata.keywords.length < 1) {
                return null;
            }

            let best: string = null;
            const matches = image.metadata.keywords.filter(imageKeyword => {
                const isMatched = allKeywords.find(keywords => {
                    return imageKeyword.description.toLocaleLowerCase().includes(keywords.toLocaleLowerCase());
                });

                if (isMatched && (!best || best.length > imageKeyword.description.length)) {
                    best = imageKeyword.description;
                }

                return isMatched;
            });

            if (!best || matches.length < 1) {
                return null;
            }

            return {
                best: best,
                count: matches.length
            };
        });
    }

    onImageLoad(): void {
        this.imageLoaded.emit();
    }

    handlePreferredImageChange(image: ImageModel): void {
        this.preferredImageChanged.emit(image);
    }
}

